import Header from '@/components/Header'
import Footer from '@/components/Footer'
import Breadcrumb from '@/components/Breadcrumb'
import MetaTags from '@/components/MetaTags'
import SEOSchemaMarkup from '@/components/SEOSchemaMarkup'
import CTABanner from '@/components/CTABanner'
import { useScrollAnimation } from '@/hooks/use-scroll-animation'
import photoEditing from '@/assets/photo-editing.webp'
import photoEditingAntes from '@/assets/photo-editing-antes.webp'
import photoEditingDepois from '@/assets/photo-editing-depois.webp'
import layoutExclusivo1 from '@/assets/layout-exclusivo-1.webp'
import layoutExclusivo2 from '@/assets/layout-exclusivo-2.webp'
import layoutExclusivo3 from '@/assets/layout-exclusivo-3.webp'
import layoutExclusivo4 from '@/assets/layout-exclusivo-4.webp'
import curadoriaImg from '@/assets/curadoria-new.webp'
import curadoriaImg2 from '@/assets/curadoria-new-2.webp'
import { Dialog, DialogContent } from '@/components/ui/dialog'

import React, { useEffect, useState } from 'react'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from '@/components/ui/carousel'

const Diferenciais = () => {
  const photoEditingAnimation = useScrollAnimation({ threshold: 0.2 })
  const curadoriaAnimation = useScrollAnimation({ threshold: 0.2 })
  const layoutAnimation = useScrollAnimation({ threshold: 0.2 })
  const aprovacaoAnimation = useScrollAnimation({ threshold: 0.2 })
  const [selectedImage, setSelectedImage] = useState<string | null>(null)
  const [carouselApi, setCarouselApi] = React.useState<CarouselApi>()

  useEffect(() => {
    if (!carouselApi) return

    const autoplay = setInterval(() => {
      carouselApi.scrollNext()
    }, 5000)

    return () => clearInterval(autoplay)
  }, [carouselApi])

  return (
    <>
      <MetaTags
        title="Diferenciais - FotoLivro Store | Edição Profissional"
        description="Conheça os diferenciais da FotoLivro Store: edição profissional de fotos, curadoria especializada, layout exclusivo e processo de aprovação para fotolivros perfeitos."
        type="website"
        keywords="edição profissional fotos, curadoria fotos, layout fotolivro, diferenciais fotolivro, qualidade fotolivro"
      />
      <SEOSchemaMarkup type="organization" />

      <div className="min-h-screen flex flex-col">
        <Header />
        <Breadcrumb />
        <main
          className="flex-1 bg-background"
          role="main"
          aria-label="Nossos Diferenciais"
        >
          {/* Edição Profissional de Fotos */}
          <article
            className="py-16 bg-background"
            aria-labelledby="editing-heading"
          >
            <div
              ref={photoEditingAnimation.ref}
              className={`container mx-auto px-4 transition-all duration-700 ${
                photoEditingAnimation.isVisible
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-10'
              }`}
            >
              <header className="text-center mb-12">
                <h1
                  id="editing-heading"
                  className="font-serif font-semibold text-3xl md:text-4xl text-foreground mb-3"
                >
                  Edição Profissional das Fotos:{' '}
                  <span className="text-gold italic">
                    A Harmonia Visual que Faz a Diferença
                  </span>
                </h1>
              </header>
              <div className="text-center text-muted-foreground max-w-3xl mx-auto mb-10 text-base leading-relaxed space-y-4">
                <p>
                  Cada foto do seu álbum passa pelo trabalho minucioso da nossa
                  equipe de especialistas, que edita manualmente cada imagem sem
                  recorrer a filtros automáticos. Ajustamos cores, brilho,
                  contraste, nitidez e iluminação para que cada detalhe seja
                  realçado, cortamos imagens quando necessário e removemos
                  elementos que possam interferir na composição.
                </p>
                <p>
                  O resultado é que cada página do álbum ganha vida própria:
                  cores vibrantes, detalhes nítidos e um equilíbrio visual
                  perfeito que transforma suas memórias em fotos
                  impressionantes. Cada imagem é valorizada e cada momento
                  capturado se destaca, garantindo que o álbum final seja uma
                  experiência visual harmoniosa e memorável.
                </p>
              </div>
              <figure className="max-w-4xl mx-auto">
                <Carousel
                  className="w-full"
                  setApi={setCarouselApi}
                  opts={{ loop: true }}
                >
                  <CarouselContent>
                    <CarouselItem>
                      <div className="rounded-2xl overflow-hidden relative">
                        <img
                          src={photoEditing}
                          alt="Exemplo de edição profissional: comparação antes e depois do tratamento de cores e iluminação"
                          className="w-full h-auto"
                          loading="lazy"
                          width={800}
                          height={450}
                        />
                        <div className="absolute top-2 left-2 md:top-4 md:left-4 bg-background/90 backdrop-blur-sm px-1.5 py-0.5 md:px-3 md:py-1.5 rounded md:rounded-md border border-border shadow-md">
                          <span className="text-[10px] md:text-sm font-bold text-foreground">
                            ANTES
                          </span>
                        </div>
                        <div className="absolute top-2 right-2 md:top-4 md:right-4 bg-background/90 backdrop-blur-sm px-1.5 py-0.5 md:px-3 md:py-1.5 rounded md:rounded-md border border-border shadow-md">
                          <span className="text-[10px] md:text-sm font-bold text-foreground">
                            DEPOIS
                          </span>
                        </div>
                      </div>
                    </CarouselItem>
                    <CarouselItem>
                      <div className="rounded-2xl overflow-hidden relative grid grid-cols-2 gap-0">
                        <div className="relative">
                          <img
                            src={photoEditingAntes}
                            alt="Exemplo de edição profissional: foto original - Lençóis Maranhenses"
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                          <div className="absolute top-2 left-2 md:top-4 md:left-4 bg-background/90 backdrop-blur-sm px-1.5 py-0.5 md:px-3 md:py-1.5 rounded md:rounded-md border border-border shadow-md">
                            <span className="text-[10px] md:text-sm font-bold text-foreground">
                              ANTES
                            </span>
                          </div>
                        </div>
                        <div className="relative">
                          <img
                            src={photoEditingDepois}
                            alt="Exemplo de edição profissional: foto editada - Lençóis Maranhenses"
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                          <div className="absolute top-2 right-2 md:top-4 md:right-4 bg-background/90 backdrop-blur-sm px-1.5 py-0.5 md:px-3 md:py-1.5 rounded md:rounded-md border border-border shadow-md">
                            <span className="text-[10px] md:text-sm font-bold text-foreground">
                              DEPOIS
                            </span>
                          </div>
                        </div>
                      </div>
                    </CarouselItem>
                  </CarouselContent>
                  <CarouselPrevious
                    className="left-4"
                    aria-label="Imagem anterior"
                  />
                  <CarouselNext
                    className="right-4"
                    aria-label="Próxima imagem"
                  />
                </Carousel>
              </figure>
            </div>
          </article>

          {/* Curadoria de Fotos */}
          <section
            className="py-16 bg-muted/20"
            aria-labelledby="curation-heading"
          >
            <div
              ref={curadoriaAnimation.ref}
              className={`container mx-auto px-4 transition-all duration-700 ${
                curadoriaAnimation.isVisible
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-10'
              }`}
            >
              <header className="text-center mb-12">
                <h2
                  id="curation-heading"
                  className="font-serif font-semibold text-3xl md:text-4xl text-foreground mb-3"
                >
                  Curadoria:{' '}
                  <span className="text-gold italic">
                    Encontrando a Essência da Sua História
                  </span>
                </h2>
              </header>
              <div className="text-center text-muted-foreground max-w-3xl mx-auto mb-10 text-base leading-relaxed space-y-4">
                <p>
                  Sabemos que escolher as melhores fotos para um álbum, dentre
                  centenas, pode ser uma tarefa desafiadora. O medo de deixar um
                  momento importante de fora ou de criar uma sequência
                  repetitiva e sem emoção é real.
                </p>
                <p>
                  Com um olhar treinado para a narrativa, nossa equipe de
                  curadores encontra o fio condutor da sua história.
                  Selecionamos as imagens que, juntas, criam ritmo, emoção e
                  fluidez, garantindo que seu álbum seja emocionante do início
                  ao fim. É a tranquilidade de saber que sua história será
                  contada da melhor forma possível.
                </p>
              </div>
              <figure className="max-w-5xl mx-auto px-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <img
                  src={curadoriaImg}
                  alt="Curadoria de fotos: celular com galeria de fotos sobre álbum aberto com paisagem de montanhas"
                  className="w-full h-auto rounded-2xl object-cover cursor-pointer hover:opacity-90 transition-opacity"
                  loading="lazy"
                  onClick={() => setSelectedImage(curadoriaImg)}
                />
                <img
                  src={curadoriaImg2}
                  alt="Curadoria de fotos: celular com galeria de fotos sobre álbum aberto com paisagem de Lençóis Maranhenses"
                  className="w-full h-auto rounded-2xl object-cover cursor-pointer hover:opacity-90 transition-opacity"
                  loading="lazy"
                  onClick={() => setSelectedImage(curadoriaImg2)}
                />
              </figure>
            </div>
          </section>

          {/* Layout */}
          <section
            className="py-16 bg-background"
            aria-labelledby="layout-heading"
          >
            <div
              ref={layoutAnimation.ref}
              className={`container mx-auto px-4 transition-all duration-700 ${
                layoutAnimation.isVisible
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-10'
              }`}
            >
              <header className="text-center mb-12">
                <h2
                  id="layout-heading"
                  className="font-serif font-semibold text-3xl md:text-4xl text-foreground mb-3"
                >
                  Layout Exclusivo:{' '}
                  <span className="text-gold italic">
                    Sua História, Contada de Forma Única
                  </span>
                </h2>
              </header>
              <div className="text-center text-muted-foreground max-w-3xl mx-auto mb-10 text-base leading-relaxed space-y-4">
                <p>
                  Criar o layout de um álbum é muito mais do que apenas encaixar
                  fotos nas páginas. Nossos designers não usam modelos prontos;
                  cada página é pensada estrategicamente para criar ritmo,
                  destacar os momentos mais importantes e guiar o olhar de forma
                  natural. O resultado é um design clean e atemporal que
                  valoriza suas fotografias e transforma o álbum em uma
                  narrativa visual única, que dá vida à sua história.
                </p>
              </div>
              <figure className="max-w-4xl mx-auto">
                <div className="rounded-2xl overflow-hidden grid grid-cols-2 md:grid-cols-4 gap-4">
                  <img
                    src={layoutExclusivo1}
                    alt="Exemplo de layout exclusivo: álbum de viagem com design personalizado"
                    className="w-full h-auto rounded-xl object-cover aspect-[3/4] cursor-pointer hover:opacity-90 transition-opacity"
                    loading="lazy"
                    onClick={() => setSelectedImage(layoutExclusivo1)}
                  />
                  <img
                    src={layoutExclusivo2}
                    alt="Exemplo de layout exclusivo: álbum com capa personalizada"
                    className="w-full h-auto rounded-xl object-cover aspect-[3/4] cursor-pointer hover:opacity-90 transition-opacity"
                    loading="lazy"
                    onClick={() => setSelectedImage(layoutExclusivo2)}
                  />
                  <img
                    src={layoutExclusivo3}
                    alt="Exemplo de layout exclusivo: álbum de família com composição artística"
                    className="w-full h-auto rounded-xl object-cover aspect-[3/4] cursor-pointer hover:opacity-90 transition-opacity"
                    loading="lazy"
                    onClick={() => setSelectedImage(layoutExclusivo3)}
                  />
                  <img
                    src={layoutExclusivo4}
                    alt="Exemplo de layout exclusivo: álbum de bebê com design elegante"
                    className="w-full h-auto rounded-xl object-cover aspect-[3/4] cursor-pointer hover:opacity-90 transition-opacity"
                    loading="lazy"
                    onClick={() => setSelectedImage(layoutExclusivo4)}
                  />
                </div>
              </figure>

              {/* Lightbox Dialog */}
              <Dialog
                open={!!selectedImage}
                onOpenChange={() => setSelectedImage(null)}
              >
                <DialogContent className="max-w-4xl p-2 bg-background/95 backdrop-blur-sm border-none">
                  {selectedImage && (
                    <img
                      src={selectedImage}
                      alt="Imagem ampliada"
                      className="w-full h-auto max-h-[85vh] object-contain rounded-lg"
                    />
                  )}
                </DialogContent>
              </Dialog>
            </div>
          </section>

          {/* Aprovação */}
          <section
            className="py-16 bg-muted/20"
            aria-labelledby="approval-heading"
          >
            <div
              ref={aprovacaoAnimation.ref}
              className={`container mx-auto px-4 transition-all duration-700 ${
                aprovacaoAnimation.isVisible
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-10'
              }`}
            >
              <header className="text-center mb-12">
                <h2
                  id="approval-heading"
                  className="font-serif font-semibold text-3xl md:text-4xl text-foreground mb-3"
                >
                  Aprovação do álbum:{' '}
                  <span className="text-gold italic">
                    tranquilidade e segurança antes da impressão
                  </span>
                </h2>
              </header>
              <div className="text-center text-muted-foreground max-w-3xl mx-auto mb-10 text-base leading-relaxed space-y-4">
                <p>
                  Sua tranquilidade é nosso compromisso. Antes da impressão,
                  você recebe uma prova digital completa do seu álbum para
                  aprovação, para revisar cada detalhe e solicitar os ajustes
                  que desejar. A produção só acontece após a sua confirmação
                  final, garantindo um resultado fiel ao que você imaginou.
                </p>
              </div>
            </div>
          </section>

          {/* CTA Banner */}
          <aside
            className="container mx-auto px-4 pb-16"
            aria-label="Chamada para ação"
          >
            <CTABanner
              title="Comece Seu Álbum Hoje"
              description="Sua história merece ser contada com sofisticação. Pré-selecione suas fotos e deixe nossa equipe de especialistas transformá-las em um álbum exclusivo, com design profissional e qualidade impecável."
              buttonText="Iniciar meu álbum"
              buttonLink="/#produtos"
            />
          </aside>
        </main>
        <Footer />
      </div>
    </>
  )
}

export default Diferenciais
